import React, { useState, useRef, useEffect } from "react";
import { ref, set, update, onValue } from "firebase/database";
import { db } from "../firebase";

export default function ProfilePage({
  user,
  profilePic,
  setProfilePic,
  setView,
}) {
  const fileInputRef = useRef(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [userName, setUserName] = useState("");

  // Fetch current name from DB on load
  useEffect(() => {
    const nameRef = ref(db, `users/${user.uid}/profile/displayName`);
    const unsubscribe = onValue(nameRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserName(snapshot.val());
      } else {
        setUserName(user.email.split("@")[0]);
      }
    });
    return () => unsubscribe();
  }, [user.uid, user.email]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsUpdating(true);

    try {
      const profileRef = ref(db, `users/${user.uid}/profile`);
      await update(profileRef, {
        displayName: userName,
        email: user.email,
      });

      setShowSuccess(true);
      setIsUpdating(false);
      setTimeout(() => setView("dashboard"), 2000);
    } catch (error) {
      console.error("Error updating profile:", error);
      setIsUpdating(false);
    }
  };

  // --- NEW: Compressed Image Handling ---
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;

        img.onload = async () => {
          // Create Canvas for Compression
          const canvas = document.createElement("canvas");
          const MAX_WIDTH = 300; // Small size for fast sync
          const scaleSize = MAX_WIDTH / img.width;
          canvas.width = MAX_WIDTH;
          canvas.height = img.height * scaleSize;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Convert to lightweight JPEG (quality 0.7)
          const compressedBase64 = canvas.toDataURL("image/jpeg", 0.7);

          setIsUpdating(true);
          try {
            // Save the tiny string to DB
            await set(
              ref(db, `users/${user.uid}/profile/photo`),
              compressedBase64
            );
            setProfilePic(compressedBase64);
            setShowSuccess(true);
            setIsUpdating(false);
            setTimeout(() => setView("dashboard"), 2000);
          } catch (error) {
            console.error("Sync Error:", error);
            alert("Sync failed. The file might still be too large.");
            setIsUpdating(false);
          }
        };
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-6 duration-700 max-w-2xl mx-auto relative">
      {/* Success Notification */}
      {showSuccess && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[200] animate-in fade-in zoom-in">
          <div className="bg-emerald-500 text-white px-8 py-4 rounded-2xl font-black shadow-2xl flex items-center gap-3">
            <i className="fas fa-check-circle text-xl"></i>
            <span>Profile Synced Successfully!</span>
          </div>
        </div>
      )}

      <div
        className={`bg-white dark:bg-slate-900 rounded-[3rem] p-8 md:p-12 border border-slate-100 dark:border-slate-800 shadow-2xl transition-all duration-500 ${
          isUpdating ? "opacity-50 blur-sm pointer-events-none" : "opacity-100"
        }`}
      >
        <div className="flex flex-col items-center text-center">
          <h1 className="text-3xl font-black mb-10 dark:text-white uppercase tracking-tighter">
            Account Settings
          </h1>

          {/* Profile Picture Section */}
          <div className="relative group cursor-pointer mb-10">
            <div className="w-44 h-44 rounded-[3.5rem] overflow-hidden border-4 border-indigo-600 shadow-2xl relative">
              <img
                src={
                  profilePic ||
                  `https://ui-avatars.com/api/?name=${userName}&background=6366f1&color=fff`
                }
                className="w-full h-full object-cover"
                alt="Profile"
              />
              {isUpdating && (
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
            <button
              onClick={() => fileInputRef.current.click()}
              className="absolute inset-0 bg-indigo-600/60 rounded-[3.5rem] opacity-0 group-hover:opacity-100 transition-all flex flex-col items-center justify-center text-white backdrop-blur-sm"
            >
              <i className="fas fa-camera text-2xl mb-1"></i>
              <span className="text-[10px] font-black uppercase tracking-widest">
                Update
              </span>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleUpdateProfile} className="w-full space-y-6">
            <div className="text-left">
              <label className="block text-[10px] font-black text-slate-400 uppercase mb-2 ml-2 tracking-[0.2em]">
                Identity Name
              </label>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                className="w-full p-5 rounded-2xl bg-slate-50 dark:bg-slate-800 dark:text-white border-2 border-transparent focus:border-indigo-500 font-bold outline-none transition-all shadow-inner"
                required
              />
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/50 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 w-full text-left">
              <span className="block text-[10px] font-black text-slate-400 uppercase mb-1 tracking-[0.2em]">
                Registered Email
              </span>
              <p className="text-sm font-bold text-slate-500">{user.email}</p>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              accept="image/*"
              className="hidden"
            />

            <button
              type="submit"
              disabled={isUpdating}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-5 rounded-2xl font-black shadow-lg shadow-indigo-500/30 active:scale-95 transition-all disabled:bg-slate-400 uppercase tracking-widest text-xs"
            >
              {isUpdating ? "Syncing to Cloud..." : "Update Credentials"}
            </button>
          </form>

          <button
            onClick={() => setView("dashboard")}
            className="mt-8 text-slate-400 font-bold text-xs hover:text-indigo-600 transition-colors uppercase tracking-[0.2em]"
          >
            <i className="fas fa-arrow-left mr-2"></i> Cancel & Return
          </button>
        </div>
      </div>
    </div>
  );
}
